import { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';

// ASSETS
import ConnectivityIcon from 'assets/connectivityIcon';
import CalculatedOdometerIcon from 'assets/calculatedOdometerIcon';
import ReportedOdometerIcon from 'assets/reportedOdometerIcon';
import VehicleIcon from 'assets/AgreementIcons/Vehicle';

// COMPONENTS
import { OutsideLink } from '../../FleetSettings.styles';

import {
  Section,
  SectionHeader,
  SectionContent,
  SectionHeaderTitle,
  SectionHeaderActions,
  SubSection,
  SubSectionTitle,
} from 'styles/cards';
import { LargeRadioSelector } from 'components/RadioButtons';
import { SaveButton } from 'components/Buttons';
import { OdometerOptions, OdometerOptionLabel, OdometerOptionNumber } from './connectivity.styles';
import { Tag } from 'styles/common';

// SELECTORS
import { selectFleetSettings, selectLoading } from 'services/FleetSettings/selectors';
import { selectVehicles } from 'services/Vehicles/selectors';

// STYLES
import colors from 'styles/colors';

// THUNKS
import { updateSettings } from 'services/FleetSettings/thunks';
import { getVehicles } from 'services/Vehicles/thunks';

import { useAppDispatch } from 'hooks/store';
import { OdometerStrategy } from 'services/resources';

const Connectivity = () => {
  const intl = useIntl();
  const vehicles = useSelector(selectVehicles());
  const fleetSettings = useSelector(selectFleetSettings());
  const fleetSettingsLoading = useSelector(selectLoading());

  const currentValue = fleetSettings.get('preferredOdometerStrategy');

  const dispatch = useAppDispatch();
  const [selected, changeSelection] = useState(currentValue);
  const [numberReported, setNumberReported] = useState(undefined);

  useEffect(() => {
    dispatch(getVehicles(0)); // Get all dealership's vehicles, not paginated.
  }, []);

  useEffect(() => {
    // Find the number of vehicles with reported odometers
    setNumberReported(
      vehicles.get(1)?.filter((vehicle) => vehicle.get('reportedOdometer') !== null && vehicle.get('isConnected')).size
    );
  }, [vehicles]);

  return (
    <Section>
      <SectionHeader>
        <SectionHeaderTitle>
          <ConnectivityIcon color={colors.blue} />
          {intl.formatMessage({
            id: 'views.FleetSettings.Vehicles.Connectivity.title',
          })}
        </SectionHeaderTitle>
        <SectionHeaderActions>
          <SaveButton
            disabled={selected === currentValue}
            loading={fleetSettingsLoading}
            onClick={() => {
              dispatch(
                updateSettings(fleetSettings.get('id'), {
                  preferredOdometerStrategy: selected,
                })
              );
            }}
          />
        </SectionHeaderActions>
      </SectionHeader>
      <SectionContent>
        <SubSection>
          <p>
            {intl.formatMessage({
              id: 'views.FleetSettings.Vehicles.Connectivity.description',
            })}
          </p>
        </SubSection>
        <SubSection>
          <SubSectionTitle>
            {intl.formatMessage({
              id: 'views.FleetSettings.Vehicles.Connectivity.subSectionTitle',
            })}
          </SubSectionTitle>
          <p>
            {intl.formatMessage({
              id: 'views.FleetSettings.Vehicles.Connectivity.subSectionDescription',
            })}
          </p>
          <OdometerOptions>
            <LargeRadioSelector
              label={
                <OdometerOptionLabel>
                  <ReportedOdometerIcon />
                  <div>
                    {intl.formatMessage({
                      id: 'views.FleetSettings.Vehicles.Connectivity.trueOdometerTitle',
                    })}
                  </div>
                  <Tag>
                    {intl.formatMessage({
                      id: 'views.FleetSettings.Vehicles.Connectivity.recommended',
                    })}
                  </Tag>
                </OdometerOptionLabel>
              }
              justifyContent="space-between"
              name={OdometerStrategy.REPORTED}
              selected={selected}
              onClick={(name) => changeSelection(name)}
            >
              <p>
                {intl.formatMessage({
                  id: 'views.FleetSettings.Vehicles.Connectivity.trueOdometerDescription',
                })}
              </p>
              {numberReported !== undefined && (
                <OdometerOptionNumber>
                  <VehicleIcon color={colors.blue} />
                  {intl.formatMessage(
                    {
                      id: 'views.FleetSettings.Vehicles.Connectivity.trueOdometerVehicles',
                    },
                    {
                      a: (chunks) => chunks,
                      // TODO (greg): implement once vehicles can extract values from Query params
                      // <Link
                      //   key={0}
                      //   to={{
                      //     pathname: '/fleet/vehicles',
                      //     search: "",
                      //   }}
                      //   id="true-odo-vehicle-number"
                      // >
                      //   {chunks}
                      // </Link>
                      number: numberReported,
                    }
                  )}
                </OdometerOptionNumber>
              )}
            </LargeRadioSelector>
            <LargeRadioSelector
              label={
                <OdometerOptionLabel>
                  <CalculatedOdometerIcon />
                  <div>
                    {intl.formatMessage({
                      id: 'views.FleetSettings.Vehicles.Connectivity.calculatedOdometerTitle',
                    })}
                  </div>
                </OdometerOptionLabel>
              }
              justifyContent="space-between"
              name={OdometerStrategy.CALCULATED}
              selected={selected}
              onClick={(name) => changeSelection(name)}
            >
              <p>
                {intl.formatMessage({
                  id: 'views.FleetSettings.Vehicles.Connectivity.calculatedOdometerDescription',
                })}
              </p>
              {numberReported !== undefined && (
                <OdometerOptionNumber>
                  <VehicleIcon color={colors.blue} />
                  {intl.formatMessage(
                    {
                      id: 'views.FleetSettings.Vehicles.Connectivity.calculatedOdometerVehicles',
                    },
                    {
                      a: (chunks) => chunks,
                      // TODO (greg): implement once vehicles can extract values from Query params
                      // <Link
                      //   key={0}
                      //   id="calculated-odo-vehicle-number"
                      //   to={{
                      //     pathname: '/fleet/vehicles',
                      //     search: "",
                      //   }}
                      // >
                      //   {chunks}
                      // </Link>
                      number: vehicles.get(1).filter((vehicle) => vehicle.get('isConnected')).size - numberReported,
                    }
                  )}
                </OdometerOptionNumber>
              )}
            </LargeRadioSelector>
          </OdometerOptions>
          <OutsideLink
            target="_blank"
            rel="noopener"
            href={intl.formatMessage({
              id: 'views.FleetSettings.Vehicles.Connectivity.learnMoreLink',
            })}
          >
            {intl.formatMessage({
              id: 'views.FleetSettings.Vehicles.Connectivity.learnMore',
            })}
          </OutsideLink>
        </SubSection>
      </SectionContent>
    </Section>
  );
};

export default Connectivity;
